import styled from 'helpers/styles';
import {css} from 'styled-components';
import ComponentStyles from 'styles/mixins/Component.styles';
import Box from 'components/atoms/Layout/Box/Box';
import PropTypes from 'prop-types';
import constants from 'helpers/constants';
import {rgba} from 'polished';
import utils from 'helpers/utils';

const StyledTable = styled(Box).attrs((props) => {
  let attr = props;

  const rowColorVars = props.theme.color2Css('MuiTableRow-root', null, props.$color, {
    varPrefix: 'table',
    variables: true,
    interactive: false,
    flatten: true,
    type: constants.colorTypes.accentBackground
  });

  attr = utils.styleProps(attr, rowColorVars.vars, {rowColor: rowColorVars.css});

  const cellColorVars = props.theme.color2Css('MuiTableRow-root', 'MuiTableCell-root', props.$color, {
    varPrefix: 'table',
    variables: true,
    interactive: false,
    flatten: true,
    type: constants.colorTypes.accentBackground
  });

  attr = utils.styleProps(attr, cellColorVars.vars, {cellColor: cellColorVars.css});

  const clickableRowColorVars = props.theme.color2Css('MuiTableRow-root', null, props.$color, {
    variables: true,
    varPrefix: 'table-clickable',
    interactive: true,
    flatten: true,
    type: constants.colorTypes.accentBackground
  });

  attr = utils.styleProps(attr, clickableRowColorVars.vars, {clickableRowColor: clickableRowColorVars.css});

  const clickableCellColorVars = props.theme.color2Css('MuiTableRow-root', 'MuiTableCell-root', props.$color, {
    variables: true,
    varPrefix: 'table-clickable',
    interactive: true,
    flatten: true,
    type: constants.colorTypes.accentBackground
  });

  attr = utils.styleProps(attr, clickableCellColorVars.vars, {clickableCellColor: clickableCellColorVars.css});

  const vars = {
    '--table-progress-max-height': props.$showProgressBars ? props.theme.layout(2) : '0px',
    '--table-cell-text-color': rgba(props.theme.property('palette.text.primary'), props.theme.property('palette.action.disabledOpacity')),
    '--table-editable-cell-hover-background-color': props.theme.state2Color(props.$color, 'hover', constants.colorTypes.accentBackground, constants.colorTargets.background, true),
    '--table-editable-selected-cell-hover-background-color': props.theme.state2Color(props.$color, 'selected', constants.colorTypes.accentBackground, constants.colorTargets.background, true),
    '--table-bounds-top': props.$wrapper?.boundsCum?.top ?? '0px',
    '--table-bounds-right': props.$wrapper?.boundsCum?.right ?? '0px',
    '--table-bounds-bottom': props.$wrapper?.boundsCum?.bottom ?? '0px',
    '--table-bounds-left': props.$wrapper?.boundsCum?.left ?? '0px'
  };

  return utils.styleProps(attr, vars);
})`
  width: 100%;
  height: 100%;
  
  .MuiTable-root {
    table-layout: fixed;
  }

  .MuiTableHead-root {
    z-index: ${props => props.theme.property('zIndex.header')} !important;

    .MuiTableRow-root {
      box-shadow: unset;
      min-height: ${props => props.theme.layout(36)};
      max-height: ${props => props.theme.layout(36)};
    }  
  }

  .MuiTableBody-root {
    min-height: ${props => props.theme.layout(72)};
    
    .MuiTableRow-root {
      min-height: ${props => props.theme.layout(72)};
      max-height: ${props => props.theme.layout(72)};
    }
    
    tr {
      ${props => props.$css.rowColor}
      ${props => props.$css.cellColor}

      td.TableCell:hover {
        outline: unset;
      }
      
      td {
        > p.MuiTypography-root {
          padding: ${props => props.theme.spacing(3)} 0;
          color: ${props => props.$css.$var('--table-cell-text-color')};
        }
      }
      
      &.clickable {
        cursor: pointer;
        ${props => props.$css.clickableRowColor}
        ${props => props.$css.clickableCellColor}
        
        td.TableCell-editable:hover {
          background-color: ${props => props.$css.$var('--table-editable-cell-hover-background-color', false)} !important;
        }

        &.Mui-selected {
          td.TableCell-editable:hover {
            background-color: ${props => props.$css.$var('--table-editable-selected-cell-hover-background-color', false)} !important;
          }
        }
      }
    }
  }
  
  .MuiTableHead-root, .MuiTableBody-root {
    tr {
      .TableHeader, .TableCell {
        border-bottom: 1px solid ${props => props.theme.property('palette.divider')};
      }
      
      &:last-child {
        .TableCell {
          ${props => props.$showProgressBars ? css`
            border-bottom: unset;
          ` : css`
            border-bottom: 1px solid ${props => props.theme.property('palette.divider')};
          `}
        }
      }
    }
  }

  .MuiTableContainer-root {
    border-radius: 0;
  }
  
  .MuiTableContainer-root + .MuiToolbar-root {
    box-shadow: unset;
    
    .MuiLinearProgress-root {
      height: ${props => props.theme.layout(3)};
    }
  }
  
  .TableHeader {
    ${props => props.theme.font2Css('tableHeader')};
    justify-content: center;
    padding: ${props => props.theme.layout('0.5sp')} ${props => props.theme.layout('1.5sp')};

    user-select: none;
    -webkit-user-drag: none;
    
    &-mrt-row-select {
      margin-top: -${props => props.theme.layout('0.25sp')};
      padding: ${props => props.theme.layout('0.5sp')} ${props => props.theme.layout('1.5sp')};
    }
      
    &.MuiTableCell-alignCenter {
      .Mui-TableHeadCell-Content {
        &-Labels {
          margin-left: -${props => props.theme.layout('1.5sp')};
        }
      }
      
      &:has(.ColumnActionMenu) {
        .Mui-TableHeadCell-Content {
          &-Labels {
            margin-left: 0;
            padding-left: ${props => props.theme.layout('2sp')};
          }
        }
      }

      &:not(:has(.MuiTableSortLabel-root)) {
        .Mui-TableHeadCell-Content {
          &-Labels {
            padding-left: 0;
            padding-right: ${props => props.theme.layout('1sp')};;
          }
        }
      }
      
      .Mui-TableHeadCell-Content {
        &-Labels {
          justify-content: center;
          * {
            justify-content: center;
          }
        }
      }
    }
    
    .Mui-TableHeadCell-Content {
      &-Labels {
        flex-grow: 1;
        align-self: center;
        padding-left: 0;
      }

      .MuiTableSortLabel-root {
        width: unset;
        transform: unset !important;
        .MuiSvgIcon-root {
          font-size: ${props => props.theme.fontSize(16)};
        }
      }
      
      &-Actions {
        display: flex;
        align-items: center;
        gap: ${props => props.theme.layout('1sp')};
        margin-right: -${props => props.theme.spacing(0.5)};

        .ColumnActionMenu {
          width: ${props => props.theme.layout('2sp')};
          height: ${props => props.theme.layout('2sp')};

          .MuiTouchRipple-root {
            top: ${props => props.theme.layout(2)};
            left: ${props => props.theme.layout(2)};
          }
        }
        .MuiSvgIcon-root {
          width: ${props => props.theme.layout(12)};
        }
      }

      &-Wrapper {
        text-overflow: ellipsis;
      }

      > .MuiBox-root:last-child {
        &.Mui-TableHeadCell-ResizeHandle-Wrapper {
          > hr, div {
            background-color: ${props => props.theme.property('palette.divider')};
            border-color: ${props => props.theme.property('palette.divider')};
            border-width: ${props => props.theme.spacing(1 / 8)};
            height: ${props => props.theme.spacing('1.25f')};
          }

          &:active {
            > hr, div {
              background-color: ${props => props.theme.property('palette.primary.main')};
              border-color: ${props => props.theme.property('palette.primary.main')};
            }
          }
        }
      }

      &:has(.TableHeaderIcon) {
        .Mui-TableHeadCell-Content-Wrapper {
          min-width: 0;
        }
      }
    }
  }

  .TableCell {
    ${props => props.theme.font2Css('tableBody')};

    padding: ${props => props.theme.layout('1sp')} ${props => props.theme.layout('1.5sp')};
    
    &.MuiTableCell-alignCenter {
      padding-left: 0;
      padding-right: ${props => props.theme.layout(34)};

      * {
        justify-content: center;
      }
    }
    
    &-mrt-row-select {
      padding: ${props => props.theme.layout('1sp')} ${props => props.theme.layout('1.5sp')};
    }
  }
  
  .TableHeader, .TableCell {
    &.TableHeader-hovering, &.TableHeader-dragging, &.TableCell-hovering, &.TableCell-dragging {
      border-width: 1.5px !important;
      
      .Mui-TableHeadCell-Content {
        margin-top: -1.5px;
      }
    }
    
    &-pinned {
      z-index: ${props => props.theme.property('zIndex.pinned')};

      &.TableHeader-hovering, &.TableHeader-dragging, &.TableCell-hovering, &.TableCell-dragging {
        opacity: 1;
      }
    }
    
    &-pinned-last {
      box-shadow: unset;
      &:not(.TableHeader-hovering, .TableHeader-dragging, .TableCell-hovering, .TableCell-dragging) {
        &.TableHeader-pinned-left, &.TableCell-pinned-left {
          border-right: 1px solid ${props => props.theme.property('palette.divider')};
        }

        &.TableHeader-pinned-right, &.TableCell-pinned-right {
          border-left: 1px solid ${props => props.theme.property('palette.divider')};
        }
      }
    }
  }
  
  .TableHeader, .TableCell {
    &-mrt-row-select {
      box-shadow: unset;
      max-width: ${props => props.theme.layout(40)};
      min-width: ${props => props.theme.layout(40)};

      .MuiCheckbox-root {
        height: ${props => props.theme.layout('4sp')};
        width: ${props => props.theme.layout('4sp')};
        svg {
          font-size: ${props => props.theme.fontSize(20)};
        }
      }
    }
    
    &-selected {
      background-color: inherit;
    }
  }
  
  .MuiModal-root {
    .MuiDialog-paper {
      overflow: hidden;
    }   
    .MuiPaper-root {
      display: flex;
      flex-direction: column;
    }
    .MuiToolbar-root {
      position: relative;
    }
  }
  
  .Table {
    &-footer {
      width: 100%;
      position: absolute;
      bottom: ${props => props.$css.$var('--table-bounds-bottom')};
      pointer-events: none;

      height: ${props => props.theme.layout(48)};
      background: linear-gradient(0deg, ${props => props.theme.property('palette.background.paper')} 0%, rgba(0, 0, 0, 0) 100%);
    }
  }
  
  ${props => props.$parentScroll ? css`
    .MuiTableContainer-root {
      overflow-x: auto;
      overflow-y: hidden;
      max-height: unset;
    }

    .MuiTableRow-root {
      .TableHeader-pinned-left, .TableCell-pinned-left {
        margin-left: 0;
      }
      .TableHeader-pinned-right, .TableCell-pinned-right {
        margin-right: 0;
      }

      th:not(.TableHeader), td:not(.TableCell) {
        background-color: ${props => props.theme.property('palette.background.paper')}; !important;
      }
    }

    .MuiTableBody-root {
      tr {
        td {
          > p.MuiTypography-root {
            position: sticky;
            width: 100%;
            
            left: ${props => props.$css.$var('--table-bounds-left')};
            min-width: calc(100vw - (${props => props.$css.$var('--table-bounds-left')} + ${props => props.$css.$var('--table-bounds-right')}));
            max-width: calc(100vw - (${props => props.$css.$var('--table-bounds-left')} + ${props => props.$css.$var('--table-bounds-right')}));
          }
        }
      }
    }

    .MuiToolbar-root {
      position: sticky;
      left: ${props.$css.$var('--table-bounds-left')};
      width: calc(100vw - (${props => props.$css.$var('--table-bounds-left')} + ${props => props.$css.$var('--table-bounds-right')}));
    }

    .Table {
      &-footer {
        position: fixed;
        width: calc(100vw - (${props => props.$css.$var('--table-bounds-left')} + ${props => props.$css.$var('--table-bounds-right')}));
      }
    }
  `: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    position: relative;
    
    .MuiPaper-root {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;

      .MuiTableContainer-root {
        height: 100%;
      }
    }
  `};
    
  ${props => props.$sticky ? css`
    .MuiTableContainer-root {
      .MuiTableHead-root, .MuiTableFooter-root {     
        opacity: 1;
        z-index: ${props.theme.property('zIndex.appBar')};
        
        th {
          top: ${props => props.$css.$var('--table-bounds-top')};
        }
      }
    }
    
    ${props.$parentScroll ? css`
      .MuiTableContainer-root {
        overflow: unset;
        max-height: unset;
      }
    `: null};
  `: null};
  
  ${props => props.$minimalFooter ? css`
    > .MuiPaper-root {
      > .MuiToolbar-root {
        min-height: unset;
        max-height: ${props.$css.$var('--table-progress-max-height', false)};

        .MuiLinearProgress-root {
          height: ${props => props.theme.layout(2)};
        }
      } 
    }
  ` : css``}
  
  ${ComponentStyles}
`;

StyledTable.propTypes = {
  $sticky: PropTypes.bool
};

export default StyledTable;
